import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { addDepartmentMember, removeDepartmentMember } from '../../../store/actions/organization/DepartmentActions';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import InlineAddMembersSection from '../Shared/InlineAddMembersSection';

interface EditDepartmentModalProps {
  department: any;
  onSave: (updatedDepartment: any) => Promise<void>;
  onAfterSave?: () => Promise<void>;
}

export interface EditDepartmentModalHandle {
  handleSave: () => Promise<void>;
  isSaving: boolean;
}

const EditDepartmentModal = forwardRef<EditDepartmentModalHandle, EditDepartmentModalProps>(
  ({ department, onSave, onAfterSave }, ref) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [formData, setFormData] = useState({
      name: '',
      description: '',
    });
    const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (department) {
        setFormData({
          name: department.name || '',
          description: department.description || '',
        });
        setSelectedMembers(department.members?.map((m: any) => m.id) || []);
      }
    }, [department]);

    const handleSave = async () => {
      setIsSaving(true);
      try {
        // Step 1: Update department basic info (name, description)
        await onSave({
          name: formData.name,
          description: formData.description,
        });

        // Step 2: Calculate which members were added/removed
        const originalMembers = department.members?.map((m: any) => m.id) || [];
        const membersToAdd = selectedMembers.filter(id => !originalMembers.includes(id));
        const membersToRemove = originalMembers.filter((id: any) => !selectedMembers.includes(id));

        // Step 3: Call dedicated add/remove endpoints
        for (const memberId of membersToAdd) {
          await dispatch(addDepartmentMember(department.id, memberId, 'member') as any);
        }

        for (const memberId of membersToRemove) {
          await dispatch(removeDepartmentMember(department.id, memberId) as any);
        }


        // Step 4: Refresh feed and data
        if (onAfterSave) {
          await onAfterSave();
        }
      } catch (err) {
        console.error('Error saving department:', err);
        throw err;
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({
      handleSave,
      isSaving,
    }));

    return (
      <div className="px-6 space-y-6 max-h-[60vh] w-[60vw] max-w-[60vw] overflow-y-auto custom-scrollbar-main">
        {/* Name Field */}
        <Input
          id="department-name"
          label={t('Department Name')}
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />

        {/* Description Field */}
        <Textarea
          id="department-description"
          label={t('Description')}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={5}
        />

        {/* Members Section */}
        <InlineAddMembersSection
          selectedMemberIds={selectedMembers}
          onMembersChange={setSelectedMembers}
          excludeIds={[]}
          title={t('Department Members')}
        />
      </div>
    );
  }
);

EditDepartmentModal.displayName = 'EditDepartmentModal';

export default EditDepartmentModal;
